import { Icon, NonIdealState } from "@blueprintjs/core";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Button } from "../../../../components/Button/button";
import { CreatePlaybookDialog } from "../../../../components/CreatePlaybookDialog/create-playbook-dialog";
import { TableFooter } from "../../../../components/play-table/components";
import { usePagination } from "../../../../hooks/use-pagination";
import { DEFAULT_PLAYBOOK_PAGE_SIZE } from "../../../../redux/constants";
import { Sport, useGetSport } from "../../../../redux/slices";
import { useGetAllPlaybooksQuery } from "../../../../redux/slices/playbook-api";
import { Playbook } from "../../../../types/playbook/playbook";
import { usePlaybookContext } from "../../playbook-context";
import { PlaybookPrintSettingsMenu } from "../playbook-print-settings/playbook-print-settings-menu";
import { LoadingPlaybookSelectorItem, PlaybookSelectorItem } from "../playbook-selector-item/playbook-selector-item";
import styles from "./playbook-selector.module.scss";

type PlaybookRouteParams = {
  playbookId: string;
};

export const PlaybookSelector = () => {
  const { playbookId } = useParams<PlaybookRouteParams>();
  const pagination = usePagination({ defaultPageSize: DEFAULT_PLAYBOOK_PAGE_SIZE });
  const { page, pageSize } = pagination;

  const { data: pagedData, isLoading } = useGetAllPlaybooksQuery({ page, pageSize });

  const data = pagedData?.data;
  const totalRows = pagedData?.totalRows || 0;

  const [showCreatePlaybookDialog, setShowCreatePlaybookDialog] = React.useState<boolean>(false);
  const { selectedPlaybook, setSelectedPlaybook, addingPlay, editingPrintSettings, setEditingPrintSettings } =
    usePlaybookContext();
  const sport = useGetSport();

  const hasPrintSettings = sport === Sport.BASKETBALL;

  useEffect(() => {
    if (playbookId && data) {
      const selectedPlaybook = data.find((playbook) => playbook.id === playbookId);
      if (selectedPlaybook) {
        setSelectedPlaybook(selectedPlaybook);
      }
    } else {
      // data was updated, update selected playbook to newest version of data
      const selectedPlaybookNew: Playbook | undefined = data?.find((playbook) => playbook.id === selectedPlaybook?.id);

      if (selectedPlaybookNew) {
        setSelectedPlaybook(selectedPlaybookNew);
      }
    }
  }, [playbookId, data, setSelectedPlaybook]);

  const handleClickCreatePlaybook = () => {
    setShowCreatePlaybookDialog(true);
  };

  let content = <></>;
  if (isLoading) {
    content = (
      <div className={styles.playbookItemContainer}>
        <PlaybookSelectorSkeleton />
      </div>
    );
  } else if (data?.length === 0) {
    content = (
      <div className={styles.playbookItemContainer}>
        <NoPlaybooks />
      </div>
    );
  } else {
    content = (
      <>
        <div className={styles.playbookItemContainer}>
          {editingPrintSettings ? (
            <PlaybookPrintSettingsMenu />
          ) : (
            data?.map((playbook) => (
              <PlaybookSelectorItem
                key={playbook.id}
                playbook={playbook}
                selected={playbook.id === selectedPlaybook?.id}
                onClick={() => setSelectedPlaybook(playbook)}
                setSelectedPlaybook={setSelectedPlaybook}
                showPrintSettings={hasPrintSettings && playbook.origin !== "CLASSIC"}
                onPrintSettings={(playbook) => {
                  setSelectedPlaybook(playbook);
                  setEditingPrintSettings(true);
                }}
              />
            ))
          )}
        </div>
        {!editingPrintSettings && <TableFooter totalRows={totalRows} {...pagination} />}
      </>
    );
  }

  return (
    <div
      className={classNames(styles.playbookSelector, {
        [styles.addingPlay || ""]: addingPlay,
      })}
    >
      <div className={classNames(styles.stickyPlaybooksHeader)}>
        <div className={styles.printSettingsContainer}>
          {selectedPlaybook?.origin !== "CLASSIC" && hasPrintSettings && (
            <Button outlined id="printSettingsButton" onClick={() => setEditingPrintSettings(!editingPrintSettings)}>
              <Icon icon={editingPrintSettings ? "arrow-left" : "settings"} className={styles.settingsIcon} />
              &nbsp;&nbsp; {editingPrintSettings ? "Back" : "Print Settings"}
            </Button>
          )}
        </div>
        <div className={styles.createPlaybookContainer}>
          <Button onClick={handleClickCreatePlaybook} id="createPlaybookButton">
            Create Playbook
          </Button>
        </div>
      </div>

      {content}

      <CreatePlaybookDialog
        isOpen={showCreatePlaybookDialog}
        onClose={() => setShowCreatePlaybookDialog(false)}
        playbookPlays={[]}
      />
    </div>
  );
};

const NoPlaybooks = () => {
  return <NonIdealState icon="folder-open" title="No Playbooks" description="You don't have any playbooks yet." />;
};

const PlaybookSelectorSkeleton = () => {
  return (
    <>
      {[...Array(10).keys()].map((_item, index) => (
        <LoadingPlaybookSelectorItem key={index} />
      ))}
    </>
  );
};
